import { cloneDeep } from 'lodash-es';
import { z } from 'zod';
import { MediaTypeEnum } from '@soundxyz/utils/const';
import { type ReleaseByIdQuery, ReleaseType } from '@graphql/reactQuery';
import { SAM_DISABLED_AFFILIATE_FEE_BPS } from '@utils/constants';
import type { ReleaseTrackType } from '../../subcomponents/AudioDropZone';
import type { DraftDataRewardInfo } from '../../useDraftData';
import { validateReleaseInfo } from '../Single/Shared/validation';
import { initialReleaseInfoErrors, isReleaseInfoUnnestedErrorKeysType } from './errors';
import type { UploadedMediaType } from './fields';
import {
  getEditedReleaseInfoPersistence,
  initialReleaseInfoFields,
  initialReleaseInfoTouchedFields,
  isReleaseInfoUnnestedFieldKeysType,
  isTempTrackKeys,
  ReleaseInfoFields,
  UploadedMediaSchema,
} from './fields';
import { releaseInfoState } from './fields';

const rewardSchema = z.object({
  title: z.string(),
  description: z.string(),
});

const DraftDataRewardInfoSchema = z.object({
  canCollectorsDownloadAudio: z.boolean(),
  curatorRewardsEnabled: z.boolean(),
  goldenEggImages: z.array(UploadedMediaSchema),
  rewards: z.array(rewardSchema),
});

export const clearReleaseInfoErrors = () => {
  releaseInfoState.errors = initialReleaseInfoErrors();
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setReleaseInfoField = (name: string, value: any, touched?: boolean) => {
  if (!isReleaseInfoUnnestedFieldKeysType(name)) return;

  releaseInfoState.fields[name] = value;
  releaseInfoState.touched[name] = touched || false;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setAlbumTempTrackField = (name: string, value: any, touched?: boolean) => {
  if (!isTempTrackKeys(name)) return;

  if (!releaseInfoState.fields.tempTrack) {
    releaseInfoState.fields.tempTrack = {
      tempTitle: null,
      tempCoverImage: null,
      tempAudio: null,
    };
  }

  releaseInfoState.fields.tempTrack[name] = value;

  if (touched) {
    releaseInfoState.touched.tempTrack[name] = touched;
  }
};

export const setGoldenEggImagesFieldAtRowIndex = (
  index: number,
  value: UploadedMediaType | null,
  touched?: boolean,
) => {
  if (!Array.isArray(releaseInfoState.fields.goldenEggImages)) {
    releaseInfoState.fields.goldenEggImages = [];
  }
  // Create missing values if they don't exist
  for (let i = 0; i <= index; i++) {
    if (!releaseInfoState.fields.goldenEggImages?.[i]) {
      releaseInfoState.fields.goldenEggImages[i] = null;
    }
    if (!releaseInfoState.touched.goldenEggImages[i]) {
      releaseInfoState.touched.goldenEggImages[i] = false;
    }
    if (!releaseInfoState.errors.goldenEggImages[i]) {
      releaseInfoState.errors.goldenEggImages[i] = null;
    }
  }

  releaseInfoState.fields.goldenEggImages[index] = value;
  releaseInfoState.touched.goldenEggImages[index] = touched || false;

  validateReleaseInfo();
};

export const setAdditionalRewardsFieldAtRowIndex = (
  index: number,
  name: 'title' | 'description',
  value: string,
) => {
  if (!Array.isArray(releaseInfoState.fields.rewards)) {
    releaseInfoState.fields.rewards = [];
  }
  // Create missing objects if they don't exist
  for (let i = 0; i <= index; i++) {
    if (!releaseInfoState.fields.rewards?.[i]) {
      releaseInfoState.fields.rewards[i] = { title: '', description: '' };
    }
    if (!releaseInfoState.touched.rewards[i]) {
      releaseInfoState.touched.rewards[i] = { title: false, description: false };
    }
    if (!releaseInfoState.errors.rewards[i]) {
      releaseInfoState.errors.rewards[i] = { title: null, description: null };
    }
  }

  const currentReward = releaseInfoState.fields.rewards?.[index];
  const currentTouched = releaseInfoState.touched.rewards[index];

  if (currentReward && currentTouched) {
    currentReward[name] = value;
    currentTouched[name] = true;
  }

  validateReleaseInfo();
};

export const setReleaseInfoError = (name: string, value: string | null) => {
  if (!isReleaseInfoUnnestedErrorKeysType(name)) return;
  releaseInfoState.errors[name] = value;
};

export const setAllReleaseInfoFieldsTouched = () => {
  releaseInfoState.touched = {
    title: true,
    tokenSymbol: true,
    tracks: true,
    coverImage: true,
    genre: true,
    behindTheMusic: true,
    staticCoverImage: true,
    beatsPerMinute: true,
    key: true,
    lyrics: true,
    license: true,
    location: true,
    tempTrack: {
      tempTitle: true,
      tempCoverImage: true,
      tempAudio: true,
    },
    canCollectorsDownloadAudio: true,
    curatorRewardsEnabled: true,
    goldenEggImages:
      releaseInfoState.fields.goldenEggImages?.map(() => true) ||
      initialReleaseInfoTouchedFields().goldenEggImages,
    rewards:
      releaseInfoState.fields.rewards?.map(() => ({
        title: true,
        description: true,
      })) || initialReleaseInfoTouchedFields().rewards,
  };
};

export const resetReleaseInfoState = () => {
  releaseInfoState.fields = initialReleaseInfoFields();
  releaseInfoState.errors = initialReleaseInfoErrors();
  releaseInfoState.isValid = false;
  releaseInfoState.isTempTrack = false;
  releaseInfoState.touched = initialReleaseInfoTouchedFields();
};

/**
 * When a user uploads a new cover image, reset static cover image if no gif is uploaded
 */
export const resetStaticCoverImage = () => {
  releaseInfoState.fields.staticCoverImage = null;
  releaseInfoState.touched.staticCoverImage = false;
};

// This function is triggered when a deployed release is edited
// Note: we do not support editing albums at this point
export const mapDeployedReleaseInfoToFormValues = async ({
  releaseData,
  tokenSymbol,
}: {
  /**
   * Make sure to fetch from release instead of draft, since draft might be outdated
   * once the contract is deployed
   */
  releaseData: NonNullable<NonNullable<ReleaseByIdQuery>['release']>;
  /**
   * Since tokenSymbol can't be found on release, fetch it separately from the contract
   */
  tokenSymbol: string;
}) => {
  let parsedDefaultFields: ReleaseInfoFields;

  if (!releaseData.draftId) return;

  const localStorageData = await getEditedReleaseInfoPersistence({
    releaseId: releaseData.id,
  }).get();

  // Parse because we might have stored an outdated schema if we make changes to the schema in the future
  // Fail silently if we can't parse the data, we just won't use the outdated local storage data
  const result = ReleaseInfoFields.safeParse(localStorageData);

  if (result.success) {
    // If local storage data was found, prioritize that over the data fetched from the server
    parsedDefaultFields = result.data;
  } else {
    // If no data found in local storage or if the schema is different, use the data fetched from the server
    if (!releaseData || !tokenSymbol) return;

    const mappedData = {
      title: releaseData.title,
      tokenSymbol,
      coverImage: {
        ...releaseData.coverImage,
        uploadKey: releaseData.coverImage?.key || releaseData.track.id,
        mediaType: MediaTypeEnum.RELEASE_COVER_IMAGE,
      },
      tracks: [
        {
          ...releaseData.track,
          fileDetail: {
            ...releaseData.track,
            mediaType: releaseData.track.audio.audio192k?.key
              ? MediaTypeEnum.AUDIO_192K
              : MediaTypeEnum.AUDIO,
            uploadKey:
              releaseData.track.audio.audio192k?.key || releaseData.track.audio.audioOriginal?.key,
          },
        },
      ],
      genre: releaseData.genre.name,
      behindTheMusic: releaseData.behindTheMusic,
      staticCoverImage: releaseData.staticCoverImage?.key
        ? {
            ...releaseData.staticCoverImage,
            uploadKey: releaseData.staticCoverImage?.key,
            mediaType: MediaTypeEnum.RELEASE_WEB_STATIC_IMAGE,
          }
        : null,
      beatsPerMinute: releaseData.metadataDetails?.bpm || null,
      key: releaseData.metadataDetails?.key || null,
      lyrics: releaseData.metadataDetails?.lyrics || null,
      license: releaseData.metadataDetails?.license || null,
      location: releaseData.metadataDetails?.location || null,
      tempTrack: {
        tempTitle: null,
        tempAudio: null,
        tempCoverImage: null,
      },
      canCollectorsDownloadAudio: releaseData.canCollectorsDownloadAudio,
      curatorRewardsEnabled:
        releaseData.publicAffiliateFeeBPS === SAM_DISABLED_AFFILIATE_FEE_BPS ? false : true,
      goldenEggImages: [
        {
          ...releaseData.goldenEggImage,
          uploadKey: releaseData.goldenEggImage?.key || releaseData.track.id,
          mediaType: MediaTypeEnum.RELEASE_GOLDEN_EGG_IMAGE,
        },
      ],
      rewards: releaseData.rewards,
    };

    const result = ReleaseInfoFields.safeParse(mappedData);

    if (!result.success) {
      throw new Error(`Invalid data passed to mapDeployedReleaseInfoToFormValues: ${result.error}`);
    } else {
      parsedDefaultFields = result.data;
    }
  }

  const releaseFields = {
    ...parsedDefaultFields,
    type: ReleaseType.Single, // We only support singles at this point
  };

  releaseInfoState.fields = {
    ...parsedDefaultFields,
  };

  return releaseFields;
};

// This function is triggered when data was fetched from the server and user navigates to a new step
// It serves as a reset function for the rewards state, using the data fetched from the server
// undoing any local changes the user made
export const mapDraftReleaseInfoToFormValues = ({ data }: { data: ReleaseInfoFields }) => {
  const defaultFields = {
    title: data.title,
    tokenSymbol: data.tokenSymbol,
    coverImage: data.coverImage,
    tracks: data.tracks,
    genre: data.genre,
    behindTheMusic: data.behindTheMusic,
    beatsPerMinute: data.beatsPerMinute ? Number(data.beatsPerMinute) : null,
    staticCoverImage: data.staticCoverImage,
    key: data.key,
    lyrics: data.lyrics,
    license: data.license,
    location: data.location,
    tempTrack: {
      tempTitle: data.tempTrack?.tempTitle,
      tempAudio: data.tempTrack?.tempAudio,
      tempCoverImage: data.tempTrack?.tempCoverImage,
    },
    // To support legacy releases, we need to check if the field exists
    // Older releases could have step 1 release info saved, but not rewards, which used to be step 2
    canCollectorsDownloadAudio:
      typeof data.canCollectorsDownloadAudio === 'boolean'
        ? data.canCollectorsDownloadAudio
        : initialReleaseInfoFields().canCollectorsDownloadAudio,
    curatorRewardsEnabled:
      typeof data.curatorRewardsEnabled === 'boolean'
        ? data.curatorRewardsEnabled
        : initialReleaseInfoFields().curatorRewardsEnabled,
    goldenEggImages:
      data.tracks?.map((_, index) => {
        return data.goldenEggImages?.[index] || null;
      }) || initialReleaseInfoFields().goldenEggImages,
    rewards: data.rewards?.length ? cloneDeep(data.rewards) : initialReleaseInfoFields().rewards,
  };

  const releaseFields = {
    ...defaultFields,
    type: 'SINGLE',
  };

  releaseInfoState.fields = {
    ...defaultFields,
  };

  releaseInfoState.touched = {
    title: true,
    tokenSymbol: true,
    coverImage: true,
    tracks: true,
    genre: true,
    behindTheMusic: true,
    beatsPerMinute: true,
    staticCoverImage: true,
    key: true,
    lyrics: true,
    license: true,
    location: true,
    tempTrack: {
      tempTitle: true,
      tempCoverImage: true,
      tempAudio: true,
    },
    canCollectorsDownloadAudio: true,
    curatorRewardsEnabled: true,
    goldenEggImages:
      data.goldenEggImages?.map(() => true) || initialReleaseInfoTouchedFields().goldenEggImages,
    rewards:
      data.rewards?.map(() => ({ title: true, description: true })) ||
      initialReleaseInfoTouchedFields().rewards,
  };

  validateReleaseInfo();

  return releaseFields;
};

export const setTracksAtRowIndex = (index: number, value: ReleaseTrackType) => {
  if (!releaseInfoState.fields.tracks) {
    releaseInfoState.fields.tracks = [];
  }

  if (value) {
    releaseInfoState.fields.tracks[index] = value;
  }

  validateReleaseInfo();
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setTempTrackValue = (name: string, value: any) => {
  if (!isTempTrackKeys(name)) return;

  const tempTrack = releaseInfoState.fields.tempTrack;
  const tempTrackTouched = releaseInfoState.touched.tempTrack;

  if (tempTrack) {
    tempTrack[name] = value;
  }

  if (tempTrackTouched) {
    tempTrackTouched[name] = true;
  }

  validateReleaseInfo();
};

export const getGeneratedTokenSymbol = (slug: string) => {
  // Remove dashes from slug
  // Only take syllables
  // Avoid strings longer than 8 characters
  return slug
    .split('-')
    .join('')
    .replace(/[aeiou]/gi, '')
    .toUpperCase()
    .slice(0, 8);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isDraftDataRewardInfo(obj: any): obj is DraftDataRewardInfo {
  const result = DraftDataRewardInfoSchema.safeParse(obj);
  return result.success;
}
